//  ==========================================================================
//
//  Person (detail page)
//
//  ==========================================================================
import React from 'react';
import { graphql } from 'gatsby';
import striptags from 'striptags';
import { MetaTags, CategoryBlock, RelatedContentBlock, PersonHeaderCardBlock, Section } from '../../components';
import { normalizePersonPage, normalizePostDeckContent, getYouTubeEmbedLink } from '../../helpers';
import { css } from 'emotion';
import styled from 'react-emotion';
import {
	mq,
} from '../../global-styles';

const videoGrid = ( block, i ) => {
	const embedLink = getYouTubeEmbedLink( block?.attributes?.url );
	
	return (
		<>
			{ embedLink && 
				<StyledVideoWrapper 
					className="blocks--video-youtube"
				>
					<iframe title={`video-block-${  i}`} className={block.attributes?.className} src={getYouTubeEmbedLink( block.attributes.url )} frameBorder="0">
					</iframe>
				</StyledVideoWrapper>
			}
		</>
	);
};

//  ==========================================================================
//  Component
//  ==========================================================================
const Person = ( { data, pageContext } ) => {

	const imageData = {
		wideAttachment: data.imageWideData,
		squareAttachment: data.imageSquareData
	};

	// Consolidate News, Announcements and Events data
	data['newsAnnouncementsData'] = { edges: JSON.parse( JSON.stringify( data.newsData.edges ) ) };
	data['newsAnnouncementsData']['edges'] = data['newsAnnouncementsData']['edges'].concat( JSON.parse( JSON.stringify( data.announcementsData.edges ) ), JSON.parse( JSON.stringify( data.eventsData.edges ) ) );

	// Content Normalization
	const relatedContent = normalizePostDeckContent( data.relatedContentData, pageContext.relatedContentCuratedPostIDs );
	const storiesContent = normalizePostDeckContent( data.storiesData, pageContext.curatedPostIDs['stories'] );
	const awardsContent = normalizePostDeckContent( data.awardsData, pageContext.curatedPostIDs['awards'] );
	const caseStudiesContent = normalizePostDeckContent( data.caseStudiesData, pageContext.curatedPostIDs['caseStudies'] );
	const newsAnnouncementsContent = normalizePostDeckContent( data.newsAnnouncementsData, pageContext.curatedPostIDs['newsAnnouncements'], true );
	const reportsContent = normalizePostDeckContent( data.reportsData, pageContext.curatedPostIDs['reports'] );
	const personData = normalizePersonPage( data.personData, data.personLocationsData, imageData );
	const {
		name,
		photo,
		shortBio,
		meta,
	} = personData;
	const editorBlocks = ( Array.isArray( personData.editorBlocks ) ) ? 
		personData.editorBlocks :
		[];
	const robots = meta?.hide_in_search ? 'noindex' : '';
	const { yoast } = data.personData;

	// Debugging
	if ( 'development' === process.env.ENVIRONMENT && 'object' === typeof window ) {
		console.log( 'pageContext', pageContext );
		console.log( 'data', data );
	}

	return (
		<main id="main">
			<MetaTags
				post_title={name}
				description={striptags( shortBio )}
				ogImage={photo?.imageAttachment?.file_url}
				robots={robots}
				yoast={yoast}
			/>
			<div className="content-contain content-contain--people-person">
				{
					editorBlocks.map( ( block, i ) => {
						if ( 'hks-blocks/person-card' == block.name ) {
							return (
								<StyledBlockSection key={i}>
									<PersonHeaderCardBlock
										personData={personData}
										photo={photo}
									/>
								</StyledBlockSection>
							);
						} else if ( 'hks-blocks/post-deck' === block.name && block?.attributes?.contentType ) {
							
							let posts;
							switch( block?.attributes?.contentType ) {
									case 'article':
										posts = storiesContent || []; 
										break;
									case 'award':
										posts = awardsContent || []; 
										break;
									case 'case_study':
										posts = caseStudiesContent || []; 
										break;
									case 'news_announcement':
										posts = newsAnnouncementsContent || []; 
										break;
									case 'research':
										posts = reportsContent || []; 
										break;
									default:
										posts = [];
										break;
							}					

							return ( posts.length ) ? 
								<StyledBlockSection key={i}>
									<CategoryBlock
										posts={posts}
										contentType={block.attributes.contentType}
										template="person"
									/>
								</StyledBlockSection> :
								null;
						} 
						else if ( 
							'hks-blocks/related-content' === block.name 
							&& relatedContent
							&& relatedContent.length
						) { 
							return (
								<StyledBlockSection key={i}>
									<RelatedContentBlock
										posts={relatedContent}
										template="person"
									/>
								</StyledBlockSection>
							);
						} else if (
							'core-embed/youtube' === block.name
						) {
							const embedLink = getYouTubeEmbedLink( block?.attributes?.url );
							return embedLink ? 
								<StyledBlockSection key={i}>
									<Section
										post_title="Videos"
										titleVisible={true}
										titleStyle="bar"
										template="person"
									>
										<StyledVideoWrapper 
											className="blocks--video-youtube"
										>
											<iframe title={`video-block-${  i}`} className={block.attributes?.className} src={getYouTubeEmbedLink( block.attributes.url )} frameBorder="0">
											</iframe>
										</StyledVideoWrapper>
									</Section>
								</StyledBlockSection> :
								null;
						} else if (
							'video-group' === block.name
						) {
							return block.videos ? 
								<StyledBlockSection key={i}>
									<Section
										post_title="Videos"
										titleVisible={true}
										titleStyle="bar"
										template="person"
									>
										<StyledVideoGrid>
											{ block.videos.map( videoGrid ) }
										</StyledVideoGrid>
									</Section>
								</StyledBlockSection> :
								null;
						} else {
							return null;
						}
					} )
				}	
			</div>
		</main>
	);
	
};

export const personQuery = graphql`
	query PersonPageQuery(
		$id: Int!, 
		$personLocations: [Int], 
		$imageFileSquare: String, 
		$imageFileWide: String,
		$relatedContentIDs: [Int]
	) {
		personData: wordsbyCollections(ID: {eq: $id}) {
			...PersonFields
			...Yoast
		}
		personLocationsData: allWordsbyCollections (
			filter: { 
				post_type:{eq:"location"},
				ID: {in: $personLocations }
			}
		) {
			edges {
				node {
					post_title
				}
			}	
		}
		imageSquareData: wordsbyAttachments(file: {eq: $imageFileSquare, nin:[null,""]}) {
			...WordsbyAttachment
		}
		imageWideData: wordsbyAttachments(file: {eq: $imageFileWide, nin:[null,""]}) {
			...WordsbyAttachment
		}
		awardsData: allWordsbyCollections(
			filter: {
				post_type: {eq: "external_link"}, 
				acf: {link_type: {eq: "award"}},
				people: {elemMatch: {ID: {in: [$id]}}}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99,
		) {
			edges {
			  	node {
					...PostDeckContentCardFields
					acf {
						link_type
						url
					}
				}
			}
		}
		announcementsData: allWordsbyCollections(
			filter: {
				post_type: {eq: "article"}, 
				acf: {article_type: {eq: "announcement"}},
				people: {elemMatch: {ID: {in: [$id]}}}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99,
		) {
			edges {
			  	node {
					...PostDeckContentCardFields
					acf {
				  		article_type
					}
				}
			}
		}
		eventsData: allWordsbyCollections(
			filter: {
				post_type: {eq: "article"}, 
				acf: {article_type: {eq: "event"}},
				people: {elemMatch: {ID: {in: [$id]}}}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99,
		) {
			edges {
			  	node {
					...PostDeckContentCardFields
					acf {
				  		article_type
					}
				}
			}
		}
		newsData: allWordsbyCollections(
			filter: {
				post_type: {eq: "external_link"}, 
				acf: {link_type: {eq: "news"}},
				people: {elemMatch: {ID: {in: [$id]}}}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99,
		) {
			edges {
			  	node {
					...PostDeckContentCardFields
					acf {
						link_type
						url
					}
				}
			}
		}
		storiesData: allWordsbyCollections(
			filter: {
				post_type: {eq: "article"}, 
				acf: {article_type: {eq: "story"}},
				people: {elemMatch: {ID: {in: [$id]}}}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99
		) {
			edges {
			  	node {
					...PostDeckContentCardFields
					acf {
				  		article_type
					}
				}
			}
		}
		caseStudiesData: allWordsbyCollections(
			filter: {
				people: {elemMatch: {ID: {in: [$id]}}}, 
				post_type: {eq: "case_study"}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99,
		) {
			edges {
			  node {
				...PostDeckContentCardFields
				acf {
					article_type
			  	}
			  }
			}
		}
		reportsData: allWordsbyCollections(
			filter: {
				people: {elemMatch: {ID: {in: [$id]}}}, 
				post_type: {eq: "research"}
			},
			sort:{
				fields: [post_date],
				order: [DESC],
			},
			limit: 99,
		) {
			edges {
			  node {
				...PostDeckContentCardFields
				acf {
					article_type
			  	}
			  }
			}
		}
		servicesData: allWordsbyCollections(
			filter: {
				people: {elemMatch: {ID: {in: [$id]}}}, 
				post_type: {eq: "service"}
			}
			limit: 99,
		) {
			edges {
				node {
					...PostDeckContentCardFields
					acf {
						article_type
					}
				}
			}
		}
		relatedContentData: allWordsbyCollections(
			filter: {
				ID: {in: $relatedContentIDs}, 
			},
			sort:{
				fields: [post_type,post_title,post_date],
				order: [DESC,ASC,DESC],
			},
			limit: 99,
		) {
			edges {
			  node {
				...RelatedContentCardFields
			  }
			}
		}
  	}
`;

export default Person;

//  ==========================================================================
//  Styles
//  ==========================================================================
const StyledBlockSection = styled( 'div' )`
	margin-bottom: 26px;
	${mq.md( css`
		margin-bottom: 70px;
	` )}
`;

const StyledVideoWrapper = styled( 'div' )`
	position:relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 0;
	padding-top: 56.25%;
	iframe {
		position: absolute;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		background: rgba(200,200,200,1);
		frameborder: 0;
		&.wp-embed-aspect-21-9 {
			padding-bottom: 42.86%;
		}
		&.wp-embed-aspect-4-3 {
			padding-bottom: 75%;
		}
		&.wp-embed-aspect-9-16 {
			padding-bottom: 177.78%;
		}
	}
`;

const StyledVideoGrid = styled( 'div' )`

	${mq.md( css`
		margin: 0 -10px;
		grid-template-columns: repeat(2,minmax(0,1fr));
		display: grid;
	` )}

	.blocks--video-youtube {
		margin-bottom:10px;
		
		${mq.md( css`
			margin-bottom:0;
		` )}

		iframe {
			background: transparent;

			${mq.md( css`
				padding:10px;
			` )}
		}

		&:first-child {
			grid-column: 1 / 3;
			iframe {
				padding-top:0;
			}
		}
	}
`;